import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

import { useNavigate } from "react-router-dom";
import { Formik, Field, Form, useFormikContext } from "formik";
import { FieldValue, arrayUnion } from "firebase/firestore";
import M from "materialize-css";

import { db } from "../lib/firebaseCompat";
import Layout from "../components/shared/Layout";
import InputField from "../components/shared/InputField";
import CardPanel from "../components/shared/CardPanel";
import CardHeader from "../components/shared/CardHeader";
import CardTitle from "../components/shared/CardSearchTable/Title";
import Row from "../components/shared/Row";

import { useMaterialInputFields } from "../hooks/useMaterialInputFields";
import { useOrganization } from "../hooks/useOrganization";
import { useAutocomplete } from "../hooks/use-autocomplete";
import { useAuth } from "../hooks/usehooks.com/use-auth";

const AutocompleteEmail = ({ readOnly, onAutocomplete }) => {
  const auth = useAuth();
  const { ref, setData } = useAutocomplete(onAutocomplete);
  const organization = useOrganization();
  const currentMemberUids = organization.current?.memberUids || [];
  const {
    values: { email },
    setFieldValue,
  } = useFormikContext();

  const field = "email";

  const handleChange = async (e) => {
    const { value } = e.target;
    setFieldValue(field, value);

    if (value.length < 4) {
      return;
    }

    const valueWild = `${value}~`;

    const query = db
      .collection("users")
      .orderBy("email")
      .startAt(value)
      .endAt(valueWild)
      .limit(10);

    const snapshot = await query.get();
    const users = snapshot.docs.map((doc) => ({ uid: doc.id, ...doc.data() }));
    const emails = users.reduce((acc, user) => {
      if (!currentMemberUids.includes(user.uid)) {
        acc[user.email] = null;
      }
      return acc;
    }, {});
    setData(emails);
  };

  return (
    <div className="col input-field s12">
      <i className="material-icons prefix">alternate_email</i>
      <Field
        id={field}
        name={field}
        type={field}
        innerRef={ref}
        onChange={handleChange}
        readOnly={readOnly}
        required
      />
      <label htmlFor={field}>Email</label>
    </div>
  );
};
AutocompleteEmail.propTypes = {
  onAutocomplete: PropTypes.func,
  readOnly: PropTypes.bool,
};

export default function NewUser() {
  useMaterialInputFields();
  const [newUser, setNewUser] = useState(true);
  const navigate = useNavigate();
  const organization = useOrganization();
  const currentOrganization = organization.current?.id;

  const handleSubmit = (values) => {
    (newUser ? handleNewUser : handleAddUser)(values);
  };

  const handleAddUser = async ({ email }) => {
    const query = db.collection("users").where("email", "==", email).limit(1);
    const snapshot = await query.get();
    const doc = snapshot.docs[0];
    const user = { ...doc.data(), uid: doc.id };
    const orgRef = db.collection("organizations").doc(currentOrganization);
    const memberRef = orgRef.collection("members").doc(user.uid);
    try {
      await memberRef.set({ role: "user" });
      await orgRef.update({
        memberUids: arrayUnion(user.uid),
      });
      M.toast({
        html: `User added! They are now part of ${organization.current.name}.`,
        completeCallback: () => navigate("/account"),
        displayLength: 2000,
      });
    } catch (err) {
      console.error(err);
      M.toast({
        html: "You don't have permission to add users.",
      });
    }
  };

  const handleNewUser = async ({ email, displayName }) => {
    const orgRef = db.collection("organizations").doc(currentOrganization);
    try {
      await orgRef
        .collection("pendingMembers")
        .add({ email, displayName, currentOrganization });
      M.toast({
        html: "User created! They will receive an email shortly.",
        completeCallback: () => navigate("/account"),
        displayLength: 2000,
      });
    } catch (err) {
      console.error(err);
      M.toast({
        html: "You don't have permission to create users.",
      });
    }
  };

  const headerPrefix = newUser ? "New User for" : "Add User to";
  const header = `${headerPrefix} ${organization.current.name}`;

  const initialUser = {
    displayName: "",
    email: "",
  };

  return (
    <Layout>
      <CardPanel>
        <CardTitle
          header={header}
          tooltip="Add a new user to this organization"
        />
        <br />
        <Formik initialValues={initialUser} onSubmit={handleSubmit}>
          {({ setFieldValue }) => (
            <Form autoComplete="off">
              <CardHeader text="Login Information" icon="lock" />
              <Row>
                <AutocompleteEmail
                  readOnly={!newUser}
                  onAutocomplete={(value) => {
                    setNewUser(false);
                    setFieldValue("email", value);
                  }}
                />
              </Row>
              {newUser && (
                <>
                  <CardHeader text="User Information" icon="person" />
                  <Row>
                    <InputField
                      name="displayName"
                      label="Display Name"
                      size="s12"
                      required
                    />
                  </Row>
                </>
              )}

              <div className="right-align">
                <button type="submit" className="btn green">
                  {newUser ? "Create User" : "Add Existing User"}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </CardPanel>
    </Layout>
  );
}
